import type { IGetCargoRes, TTripTaskForCargoReq } from 'api/swagger';
import { extractAddressFields, extractCoordinates } from 'shared/utils/address';
import type { TAddressFields, TCoordinatesFields } from 'types/location';
import { ELocationMode } from 'types/location';
import { ETripTaskType } from 'types/trips';
import { createStore } from './create-store';

export type TCargoTaskState = {
  taskType?: ETripTaskType;
  locationMode: ELocationMode;
  coordinates: TCoordinatesFields;
  address: TAddressFields;
  warehouseId?: number;
  cargo?: IGetCargoRes;
};

type TCargoTaskActions = {
  reset: () => void;
  setTaskType: (taskType: ETripTaskType | undefined) => void;
  setLocationMode: (mode: ELocationMode) => void;
  setCoordinates: (coordinates: TCoordinatesFields) => void;
  setAddress: (address: TAddressFields) => void;
  setWarehouseId: (warehouseId: number | undefined) => void;
  initializeFromTask: (task: TTripTaskForCargoReq) => void;
  setCargo: (cargo: IGetCargoRes) => void;
  clearCargo: () => void;
};

const initialAddressState: TAddressFields = {
  name: undefined,
  countryId: undefined,
  postCode: undefined,
  city: undefined,
  address: undefined,
};

const createDefaultState = (): TCargoTaskState => ({
  taskType: undefined,
  locationMode: ELocationMode.address,
  coordinates: { lat: undefined, lng: undefined },
  address: { ...initialAddressState },
  warehouseId: undefined,
  cargo: undefined,
});

export const useCargoTaskStore = createStore<TCargoTaskState & TCargoTaskActions>(
  (set, get) => ({
    ...createDefaultState(),

    reset: () =>
      set((state) => ({
        ...createDefaultState(),
        cargo: state.cargo,
      })),
    setCargo: (cargo) => set({ cargo }),

    clearCargo: () => set({ cargo: undefined }),
    setTaskType: (taskType) => {
      const { cargo } = get();

      const updateData: Partial<TCargoTaskState> = {
        taskType,
        locationMode: ELocationMode.address,
        coordinates: { lat: undefined, lng: undefined },
        address: { ...initialAddressState },
        warehouseId: undefined,
      };

      if (cargo) {
        let initialAddress = null;

        if (taskType === ETripTaskType.loading) {
          initialAddress = cargo.loadingAddress;
        } else if (taskType === ETripTaskType.unloading) {
          initialAddress = cargo.unloadingAddress;
        }

        if (initialAddress) {
          updateData.address = extractAddressFields(initialAddress);
          updateData.locationMode = ELocationMode.fromCargo;

          const coordinates = extractCoordinates(initialAddress);
          if (coordinates) {
            updateData.coordinates = coordinates;
          }
        }
      }

      set(updateData);
    },

    setLocationMode: (mode) => set({ locationMode: mode }),

    setCoordinates: (coordinates) => set({ coordinates }),

    setAddress: (address) => set({ address }),

    setWarehouseId: (warehouseId) => set({ warehouseId }),

    initializeFromTask: (task) => {
      const getLocationMode = () => {
        if (task.autoAddress === 'cargo') return ELocationMode.fromCargo;
        if (task.coordinates) return ELocationMode.coordinates;
        return ELocationMode.address;
      };

      set({
        taskType: task.type as ETripTaskType,
        warehouseId: task.warehouseId,
        locationMode: getLocationMode(),
        coordinates: task.coordinates ?? { lat: undefined, lng: undefined },
        address: task.address ?? { ...initialAddressState },
      });
    },
  }),
  'cargo-task-store',
);
